import { ButtonCore } from './ButtonCore';
import { ButtonIconProps } from 'omakase/Button/buttonIcons';
import { IconButtonProps } from './IconButton';
import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
} from '../utils/OmakasePolymorphicComponent';
import Anchor from 'components/Anchor';
import React from 'react';

export const ShrinkButtonsToIconsContext = React.createContext(false);

/** Props type. For this component, it may be displayed as an icon only, */
type ShrinkingHeaderButtonProps = IconButtonProps & Pick<ButtonIconProps, 'iconStart'>;

const ShrinkingHeaderButtonDefaultElement = 'button';
type ShrinkingHeaderButtonTypeGuard = 'button' | typeof Anchor;

/**
 * This is a button that, depending on the ShrinkButtonToIconsContext.Provider in
 * which it is rendered, will render either as a regular button or as an icon-only button
 * without any children content.
 */
const ShrinkingHeaderButton: OmakasePolymorphicComponent<
  ShrinkingHeaderButtonProps,
  typeof ShrinkingHeaderButtonDefaultElement,
  ShrinkingHeaderButtonTypeGuard
> = React.forwardRef(
  <E extends React.ElementType = typeof ShrinkingHeaderButtonDefaultElement>(
    {
      children,
      component,
      iconStart,
      ...rest
    }: OmakasePolymorphicComponentProps<
      ShrinkingHeaderButtonProps,
      E,
      ShrinkingHeaderButtonTypeGuard
    >,
    ref: React.Ref<HTMLButtonElement> | undefined
  ) => {
    const Component: React.ElementType = component || ShrinkingHeaderButtonDefaultElement;

    return React.useContext(ShrinkButtonsToIconsContext) ? (
      <ButtonCore iconOnly ref={ref} component={Component} {...rest}>
        {iconStart}
      </ButtonCore>
    ) : (
      <ButtonCore ref={ref} iconStart={iconStart} component={Component} {...rest}>
        {children}
      </ButtonCore>
    );
  }
);

export default ShrinkingHeaderButton;
